/**
 * Role helpers (platform-admin SPA local mirror).
 *
 * The canonical helper lives in
 * `packages/database/src/security/role-helpers.ts` (`hasRole`, `hasAnyRole`,
 * etc.) and is consumed by every server-side Express/Fastify service in this
 * staging repo. The platform-admin SPA does not (and per
 * `.claude/rules/staging-repos.md` cannot) take a runtime dependency on
 * `@aaimplatform/database` -- that package pulls in Prisma, pino, jsonwebtoken,
 * and the full Node runtime which is incompatible with the Vite browser bundle.
 *
 * This module mirrors the canonical helper's signature and semantics
 * verbatim so call sites read the same regardless of layer:
 *
 *   import { hasAnyRole, PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../utils/role-helpers';
 *
 *   if (!hasAnyRole(user?.roles, PLATFORM_ADMIN_EQUIVALENT_ROLES)) { ... }
 *
 * Per `.claude/rules/authz-case-insensitive.md` both sides of the compare are
 * lowercased before matching. The C13 WP-9 server-side helper normalizes
 * defensively at every call site even though `req.user.roles` is guaranteed
 * lowercase post-middleware; the SPA mirror preserves that defense because
 * the auth store may surface roles unchanged from the JWT response (mixed
 * UPPERCASE / lowercase observed empirically -- see
 * `~/.claude/projects/.../memory/feedback_role_name_case_insensitive.md`).
 */

/**
 * Role names that all carry the platform-admin permission scope by design.
 *
 * Source of truth: `staging/aaim-core-new/services/authorization/src/seed-rbac.ts`.
 * The fixture memory at
 * `~/.claude/projects/.../memory/fixture_e2etesting_user.md` is explicit:
 * services that gate on `aaim_super_admin` or `platform_admin` MUST also
 * accept `e2e_test_admin`.
 *
 * Order of entries reflects expected frequency in production traffic
 * (canonical SSO admins first, QA fixture last) and is irrelevant to the
 * ANY-of match semantics.
 */
export const PLATFORM_ADMIN_EQUIVALENT_ROLES = [
  'aaim_super_admin',
  'platform_admin',
  'e2e_test_admin',
] as const;

function normalizeOne(value: unknown): string | null {
  return typeof value === 'string' ? value.toLowerCase() : null;
}

function normalizeMany(values: readonly unknown[] | string | undefined | null): string[] {
  if (values == null) return [];
  if (typeof values === 'string') {
    const norm = normalizeOne(values);
    return norm == null ? [] : [norm];
  }
  if (!Array.isArray(values)) return [];
  const out: string[] = [];
  for (const value of values) {
    const norm = normalizeOne(value);
    if (norm != null) out.push(norm);
  }
  return out;
}

/**
 * Returns true when `roles` contains `target` ignoring case.
 */
export function hasRole(
  roles: readonly string[] | string | undefined | null,
  target: string,
): boolean {
  if (typeof target !== 'string') return false;
  const normTarget = target.toLowerCase();
  return normalizeMany(roles).includes(normTarget);
}

/**
 * Returns true when `roles` contains ANY of `targets` ignoring case.
 *
 * Empty `targets` returns false (no target == nothing matches).
 */
export function hasAnyRole(
  roles: readonly string[] | string | undefined | null,
  targets: readonly string[],
): boolean {
  if (!Array.isArray(targets) || targets.length === 0) return false;
  const normRoles = normalizeMany(roles);
  if (normRoles.length === 0) return false;
  for (const target of targets) {
    if (typeof target !== 'string') continue;
    if (normRoles.includes(target.toLowerCase())) return true;
  }
  return false;
}

/**
 * Returns true when `permissions` contains `target` ignoring case, or holds
 * the wildcard `'*'`. Mirrors `hasPermission` in
 * `packages/database/src/security/role-helpers.ts`; the server's check
 * decides, and the SPA reads it only to choose what to show.
 */
export function hasPermission(
  permissions: readonly string[] | string | undefined | null,
  target: string,
): boolean {
  if (typeof target !== 'string') return false;
  const normPerms = normalizeMany(permissions);
  if (normPerms.includes('*')) return true;
  return normPerms.includes(target.toLowerCase());
}
